// src/pages/PreCallBriefingPage.js
//
// Navigator Pre-Call Briefing — accessed via /patients/:id/pre-call-briefing
// Aggregates everything a navigator needs before a patient call in one clean view.
//
// Sections:
//   1. Patient header + risk level
//   2. Acute alert banner (if present)
//   3. Triage summary card (score, level, draw date, trajectory)
//   4. Pending action items (unchecked P1s)
//   5. Lifestyle recommendation snippet
//   6. Next appointment

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { preCallBriefingAPI } from '../services/api';
import { AppShell } from '../components/Layout';
import Icon from '../components/Icon';

// ── Risk level config ─────────────────────────────────────────────────────────
const RISK_CONFIG = {
  HIGH:     { color: 'var(--red)', bg: 'var(--red-bg)', label: 'HIGH RISK',     ring: 'var(--red)' },
  MODERATE: { color: 'var(--amber-text)', bg: 'var(--amber-bg)', label: 'MODERATE RISK', ring: 'var(--amber)' },
  LOW:      { color: 'var(--green)', bg: 'var(--green-bg)', label: 'LOW RISK',      ring: 'var(--green)' },
  UNKNOWN:  { color: 'var(--text-muted)',    bg: 'var(--bg-subtle)', label: 'UNKNOWN',       ring: 'var(--border)' },
};

const TRAJECTORY_CONFIG = {
  IMPROVING:         { icon: '↓', color: 'var(--green)', label: 'Improving' },
  STABLE:            { icon: '→', color: 'var(--amber-text)', label: 'Stable'    },
  WORSENING:         { icon: '↑', color: 'var(--red)', label: 'Worsening' },
  INSUFFICIENT_DATA: { icon: '–', color: 'var(--text-muted)',    label: 'First draw' },
};

// ── Main component ────────────────────────────────────────────────────────────
export default function PreCallBriefingPage() {
  const { id: patientId } = useParams();
  const navigate = useNavigate();
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  useEffect(() => {
    if (!patientId) return;
    setLoading(true);
    preCallBriefingAPI.get(patientId)
      .then(res => setBriefing(res.data))
      .catch(err => setError(err.response?.data?.message || 'Failed to load briefing'))
      .finally(() => setLoading(false));
  }, [patientId]);

  // DEF-004 / TC-N021 — "Pre-Call Briefing has AppShell: page renders WITHOUT
  // sidebar. Only plain Back to patient text link."
  //
  // The success path below was already wrapped in <AppShell>, but these three
  // early returns were not, so a navigator saw a bare, chrome-less page for the
  // whole duration of the fetch — and permanently if it failed. All four exits
  // from this component now render inside the shell.
  //
  // The breadcrumbs prop is also new. AppShell was being called with no
  // breadcrumbs at all, which is why the only way back was the in-page text
  // link the test called out: the topbar had nothing to navigate with.
  const breadcrumbs = [
    { label: 'Patients', to: '/patients' },
    { label: briefing?.patientName || 'Patient', to: `/patients/${patientId}` },
    { label: 'Pre-Call Briefing' },
  ];

  if (loading) return (
    <AppShell breadcrumbs={breadcrumbs}>
      <div className="pcb-loading">
        <div className="pcb-spinner" />
        <p>Preparing briefing…</p>
      </div>
    </AppShell>
  );

  if (error) return (
    <AppShell breadcrumbs={breadcrumbs}>
      <div className="pcb-error">
        <p><Icon name="alert" size={14} /> {error}</p>
        <button type="button" onClick={() => navigate(-1)} className="pcb-back-btn">Go back</button>
      </div>
    </AppShell>
  );

  if (!briefing) return (
    <AppShell breadcrumbs={breadcrumbs}>
      <div className="pcb-error">
        <p>No briefing available for this patient.</p>
        <button type="button" onClick={() => navigate(`/patients/${patientId}`)} className="pcb-back-btn">
          Back to patient
        </button>
      </div>
    </AppShell>
  );

  const risk       = RISK_CONFIG[briefing.consensusRiskLevel] || RISK_CONFIG.UNKNOWN;
  const trajectory = TRAJECTORY_CONFIG[briefing.trajectoryDirection] || TRAJECTORY_CONFIG.INSUFFICIENT_DATA;

  return (
    <AppShell breadcrumbs={breadcrumbs}>
    <div className="pcb-root">

      {/* ── Header ── */}
      <div className="pcb-header">
        <div className="pcb-header-left">
          <button type="button" className="pcb-back-btn" onClick={() => navigate(`/patients/${patientId}`)}>
            ← Back to patient
          </button>
          <div className="pcb-header-label">PRE-CALL BRIEFING</div>
          <div className="pcb-patient-name">{briefing.patientName}</div>
          <div className="pcb-patient-meta">
            {[briefing.age && `${briefing.age}y`, briefing.gender, briefing.bloodType]
              .filter(Boolean).join(' · ')}
          </div>
        </div>
        <div className="pcb-risk-badge" style={{ background: risk.bg, color: risk.color, borderColor: risk.ring }}>
          <div className="pcb-risk-score">{briefing.consensusRiskScore ?? '—'}<span>/100</span></div>
          <div className="pcb-risk-label">{risk.label}</div>
        </div>
      </div>

      {/* ── Acute alert banner ── */}
      {briefing.hasAcuteAlert && (
        <div className="pcb-acute-banner">
          <span className="pcb-acute-icon"><Icon name="alert" size={22} /></span>
          <div>
            <div className="pcb-acute-title">Acute Alert — Immediate Review Required</div>
            <div className="pcb-acute-body">{briefing.acuteAlertDetail}</div>
          </div>
        </div>
      )}

      {/* ── No triage state ── */}
      {!briefing.triageAvailable && (
        <div className="pcb-card pcb-no-triage">
          <div className="pcb-no-triage-icon"><Icon name="drop" size={30} /></div>
          <div className="pcb-no-triage-title">No triage results yet</div>
          <div className="pcb-no-triage-body">Upload a blood test PDF in Health Records to generate the patient's first triage report.</div>
        </div>
      )}

      {/* ── Main grid ── */}
      {briefing.triageAvailable && (
        <div className="pcb-grid">

          {/* ── Left column ── */}
          <div className="pcb-col-left">

            {/* Triage summary card */}
            <div className="pcb-card">
              <div className="pcb-card-title">Latest Triage</div>
              <div className="pcb-triage-row">
                <div className="pcb-triage-cell">
                  <div className="pcb-triage-cell-label">Draw Date</div>
                  <div className="pcb-triage-cell-value">
                    {briefing.lastDrawDate
                      ? new Date(briefing.lastDrawDate).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })
                      : '—'}
                  </div>
                </div>
                <div className="pcb-triage-cell">
                  <div className="pcb-triage-cell-label">Trajectory</div>
                  <div className="pcb-triage-cell-value" style={{ color: trajectory.color }}>
                    {trajectory.icon} {trajectory.label}
                    {briefing.previousConsensusScore != null && briefing.consensusRiskScore != null && (
                      <span className="pcb-trajectory-delta">
                        {' '}({briefing.previousConsensusScore} → {briefing.consensusRiskScore})
                      </span>
                    )}
                  </div>
                </div>
                <div className="pcb-triage-cell">
                  <div className="pcb-triage-cell-label">Previous Draw</div>
                  <div className="pcb-triage-cell-value">
                    {briefing.previousDrawDate
                      ? new Date(briefing.previousDrawDate).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })
                      : 'First draw'}
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="pcb-view-report-btn"
                onClick={() => navigate(`/patients/${patientId}/reports/${briefing.healthRecordId}`)}
              >
                View Full Report →
              </button>
            </div>

            {/* Pending action items */}
            <div className="pcb-card">
              <div className="pcb-card-title">
                Pending Actions
                {briefing.pendingActions?.length > 0 && (
                  <span className="pcb-pending-count">{briefing.pendingActions.length}</span>
                )}
              </div>
              {!briefing.pendingActions || briefing.pendingActions.length === 0 ? (
                <div className="pcb-all-done">
                  <span>✓</span> All action items completed
                </div>
              ) : (
                <ul className="pcb-action-list">
                  {briefing.pendingActions.map((item, i) => (
                    <li key={item.id || i} className={`pcb-action-item pcb-action-${item.priority?.toLowerCase()}`}>
                      <div className="pcb-action-header">
                        <span className={`pcb-priority-badge pcb-priority-${item.priority?.toLowerCase()}`}>
                          {item.priority}
                        </span>
                        <span className="pcb-action-category">{item.category}</span>
                        <span className="pcb-action-window">{item.window}</span>
                      </div>
                      <div className="pcb-action-title">{item.title}</div>
                      <div className="pcb-action-body">{item.body}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

          </div>

          {/* ── Right column ── */}
          <div className="pcb-col-right">

            {/* Next appointment */}
            <div className="pcb-card">
              <div className="pcb-card-title">Next Appointment</div>
              {briefing.nextAppointment ? (
                <div className="pcb-appt">
                  <div className="pcb-appt-date">
                    {new Date(briefing.nextAppointment.scheduledAt).toLocaleDateString('en-IN', {
                      weekday:'long', day:'numeric', month:'long', year:'numeric',
                    })}
                  </div>
                  <div className="pcb-appt-time">
                    {new Date(briefing.nextAppointment.scheduledAt).toLocaleTimeString('en-IN', {
                      hour:'2-digit', minute:'2-digit',
                    })}
                  </div>
                  {briefing.nextAppointment.doctorName && (
                    <div className="pcb-appt-doctor">{briefing.nextAppointment.doctorName}</div>
                  )}
                  {briefing.nextAppointment.appointmentType && (
                    <div className="pcb-appt-type">{briefing.nextAppointment.appointmentType}</div>
                  )}
                </div>
              ) : (
                <div className="pcb-no-appt">No upcoming appointments scheduled.</div>
              )}
            </div>

            {/* Lifestyle snippet */}
            <div className="pcb-card">
              <div className="pcb-card-title">
                Lifestyle Recommendation
                <span className="pcb-layer-badge">Layer 3</span>
              </div>
              {briefing.lifestyleAvailable ? (
                <>
                  <p className="pcb-lifestyle-text">{briefing.lifestyleSnippet}</p>
                  <button
                    type="button"
                    className="pcb-view-report-btn"
                    onClick={() => navigate(`/patients/${patientId}/reports/${briefing.healthRecordId}`)}
                  >
                    Read full recommendation →
                  </button>
                </>
              ) : (
                <div className="pcb-no-lifestyle">
                  Lifestyle recommendation not yet generated.
                  Upload a new blood test to trigger Layer 3 analysis.
                </div>
              )}
            </div>

          </div>
        </div>
      )}

      <style>{PCB_CSS}</style>
    </div>
    </AppShell>
  );
}

// ── CSS ───────────────────────────────────────────────────────────────────────
const PCB_CSS = `
.pcb-root { max-width: 1100px; margin: 0 auto; padding: 32px 24px 60px; font-family: var(--font-body); }
.pcb-loading, .pcb-error { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 300px; gap: 16px; color: var(--text-secondary); }
.pcb-spinner { width: 32px; height: 32px; border: 3px solid var(--border); border-top-color: var(--amber); border-radius: 50%; animation: pcb-spin .8s linear infinite; }
@keyframes pcb-spin { to { transform: rotate(360deg); } }

.pcb-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; gap: 20px; flex-wrap: wrap; }
.pcb-header-label { font-size: 10px; font-weight: 700; letter-spacing: .12em; color: var(--amber-text); text-transform: uppercase; margin-bottom: 6px; }
.pcb-back-btn { background: none; border: 1px solid var(--border); border-radius: 8px; padding: 6px 14px; font-size: 13px; color: var(--text-primary); cursor: pointer; margin-bottom: 12px; }
.pcb-back-btn:hover { background: var(--bg-subtle); }
.pcb-patient-name { font-size: 28px; font-weight: 700; color: var(--text-primary); line-height: 1.2; }
.pcb-patient-meta { font-size: 14px; color: var(--text-muted); margin-top: 4px; }
.pcb-risk-badge { border: 2px solid; border-radius: 16px; padding: 16px 24px; text-align: center; min-width: 130px; }
.pcb-risk-score { font-size: 36px; font-weight: 800; line-height: 1; }
.pcb-risk-score span { font-size: 14px; font-weight: 400; }
.pcb-risk-label { font-size: 11px; font-weight: 700; letter-spacing: .08em; margin-top: 4px; }

.pcb-acute-banner { display: flex; align-items: flex-start; gap: 14px; background: var(--red-bg); border: 2px solid var(--red); border-radius: 12px; padding: 16px 20px; margin-bottom: 24px; }
.pcb-acute-icon { font-size: 24px; flex-shrink: 0; }
.pcb-acute-title { font-size: 15px; font-weight: 700; color: var(--red); margin-bottom: 4px; }
.pcb-acute-body { font-size: 13px; color: var(--text-primary); line-height: 1.5; }

.pcb-grid { display: grid; grid-template-columns: 1fr 380px; gap: 20px; align-items: start; }
@media (max-width: 900px) { .pcb-grid { grid-template-columns: 1fr; } }

.pcb-card { background: var(--bg-surface); border: 1px solid var(--border); border-radius: 14px; padding: 20px 22px; margin-bottom: 20px; box-shadow: 0 1px 4px rgba(0,0,0,.05); }
.pcb-card-title { font-size: 11px; font-weight: 700; letter-spacing: .09em; text-transform: uppercase; color: var(--amber-text); margin-bottom: 16px; display: flex; align-items: center; gap: 8px; }
.pcb-layer-badge { background: var(--blue-bg); color: var(--blue); font-size: 9px; padding: 2px 7px; border-radius: 99px; font-weight: 700; letter-spacing: .05em; }

.pcb-triage-row { display: flex; gap: 0; border: 1px solid var(--border); border-radius: 10px; overflow: hidden; margin-bottom: 16px; }
.pcb-triage-cell { flex: 1; padding: 12px 14px; border-right: 1px solid var(--border); }
.pcb-triage-cell:last-child { border-right: none; }
.pcb-triage-cell-label { font-size: 10px; color: var(--text-muted); font-weight: 600; text-transform: uppercase; letter-spacing: .06em; margin-bottom: 4px; }
.pcb-triage-cell-value { font-size: 14px; font-weight: 600; color: var(--text-primary); }
.pcb-trajectory-delta { font-size: 11px; font-weight: 400; color: var(--text-muted); }

.pcb-view-report-btn { background: var(--brand-dark); color: var(--text-on-brand-dark); border: none; border-radius: 8px; padding: 9px 18px; font-size: 13px; font-weight: 600; cursor: pointer; width: 100%; }
.pcb-view-report-btn:hover { background: var(--brand-navy); }

.pcb-pending-count { background: var(--red); color: var(--text-on-brand-dark); border-radius: 99px; font-size: 10px; padding: 2px 7px; font-weight: 700; }
.pcb-all-done { color: var(--green); font-size: 14px; font-weight: 500; padding: 8px 0; }
.pcb-action-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 12px; }
.pcb-action-item { border-radius: 10px; padding: 12px 14px; border: 1px solid; }
.pcb-action-p1 { border-color: var(--red); background: var(--red-bg); }
.pcb-action-p2 { border-color: var(--amber); background: var(--amber-bg); }
.pcb-action-p3 { border-color: var(--green); background: var(--green-bg); }
.pcb-action-header { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
.pcb-priority-badge { font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 4px; }
.pcb-priority-p1 { background: var(--red); color: var(--text-on-brand-dark); }
.pcb-priority-p2 { background: var(--brand-accent); color: var(--text-on-brand-dark); }
.pcb-priority-p3 { background: var(--green); color: var(--text-on-brand-dark); }
.pcb-action-category { font-size: 10px; color: var(--text-muted); font-weight: 600; text-transform: uppercase; letter-spacing: .06em; }
.pcb-action-window { font-size: 10px; color: var(--text-muted); margin-left: auto; }
.pcb-action-title { font-size: 14px; font-weight: 600; color: var(--text-primary); margin-bottom: 4px; }
.pcb-action-body { font-size: 12px; color: var(--text-secondary); line-height: 1.5; }

.pcb-appt-date { font-size: 16px; font-weight: 600; color: var(--text-primary); margin-bottom: 2px; }
.pcb-appt-time { font-size: 22px; font-weight: 700; color: var(--amber-text); margin-bottom: 6px; }
.pcb-appt-doctor { font-size: 13px; color: var(--text-secondary); margin-bottom: 2px; }
.pcb-appt-type { font-size: 11px; color: var(--text-muted); text-transform: uppercase; letter-spacing: .06em; }
.pcb-no-appt, .pcb-no-lifestyle { font-size: 13px; color: var(--text-muted); line-height: 1.5; }

.pcb-lifestyle-text { font-size: 14px; color: var(--text-primary); line-height: 1.6; margin: 0 0 14px; }

.pcb-no-triage { text-align: center; padding: 40px 20px; }
.pcb-no-triage-icon { font-size: 40px; margin-bottom: 12px; }
.pcb-no-triage-title { font-size: 18px; font-weight: 600; color: var(--text-primary); margin-bottom: 8px; }
.pcb-no-triage-body { font-size: 13px; color: var(--text-muted); line-height: 1.5; max-width: 360px; margin: 0 auto; }
`;

